import useVenueCurrency from "src/hooks/useVenueCurrency";
import PropTypes from "prop-types";
import axios from "axios";
import { useState, useEffect } from "react";
// @mui
import {
  Card,
  Stack,
  Button,
  Typography,
  CardContent,
  Grid,
  Box,
  DialogContent,
  Divider,
  TextField,
  DialogTitle,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { onCloseModalForUserOrderDetail } from "../../../../redux/slices/orderSlice";
import { StarIcon } from "src/theme/overrides/CustomIcons";
import Image from "src/components/image";
import { useAuthContext } from "src/auth/useAuthContext";
import { fetchImage } from "src/utils/fetchImage";
import Iconify from "../../../../components/iconify";
import { fNumber } from "src/utils/formatNumber";

UserOrderDetailModal.propTypes = {
  sx: PropTypes.object,
  title: PropTypes.string,
  subheader: PropTypes.string,
  onClose: PropTypes.func,
  singleData: PropTypes.array,
  onOrderAdd: PropTypes.func,
  getTable: PropTypes.func,
  modalTableData: PropTypes.func,
};

export default function UserOrderDetailModal({
  singleData,
  onOrderAdd,
  getTable,
  modalTableData,
  title,
  onClose,
  subheader,
  sx,
  ...other
}) {
  const { symbol: currencySymbol } = useVenueCurrency();
  const dispatch = useDispatch();
  const { venue } = useSelector((state) => state.venue);
  const [imageURLs, setImageURLs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { user } = useAuthContext();

  const fetchReviews = async () => {
    if (!singleData?.id) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(
        `${backendUrl}/review/byOrderId?orderId=${singleData.id}`
      );
      setReviews(response.data);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [singleData]);

  useEffect(() => {
    const fetchItemImages = async () => {
      if (singleData?.items) {
        try {
          const imagePromises = singleData?.items?.map((item) =>
            fetchImage(item?.photoURL)
          );
          const fetchedImages = await Promise.all(imagePromises);
          setImageURLs(fetchedImages);
        } catch (error) {
          console.error("Error fetching item images:", error);
        }
      }
    };

    fetchItemImages();
  }, [singleData?.items]);

  const formatTime = (time) => {
    const options = { hour: "2-digit", minute: "2-digit", hour12: true };
    return new Intl.DateTimeFormat("en-US", options).format(time);
  };

  const formatDate = (time) => {
    const options = { day: "numeric", month: "short", year: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(time);
  };

  const handleCloseModal = () => {
    dispatch(onCloseModalForUserOrderDetail());
  };

  const getOrderType = (orderType) => {
    if (orderType === "dineIn") return "DineIn";
    if (orderType === "takeaway") return "PickUp";
    return "-";
  };

  const getPaymentType = (paymentType) => {
    if (paymentType === "cash") return "Cash";
    if (paymentType === "card") return "Card";
    if (paymentType === "infini") return "Infini";
    if (paymentType === "wallet") return "Wallet";
    if (paymentType === "bankTransfer") return "Bank Transfer";
    if (paymentType === "IPG") return "IPG";
    return "-";
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <StarIcon
        key={index}
        style={{
          color: rating > index ? "#fca92e" : "#ccc",
          fontSize: "20px",
        }}
      />
    ));
  };

  const questions = [
    {
      key:
        singleData?.ordertype === "QrDineIn" ||
        singleData?.ordertype === "dineIn"
          ? "ambiance"
          : "waitTime",
      label:
        singleData?.ordertype === "QrDineIn" ||
        singleData?.ordertype === "dineIn"
          ? "Ambiance"
          : "Wait Time",
    },
    { key: "foodQuality", label: "Food Quality" },
    { key: "service", label: "Service" },
    { key: "overAllExperience", label: "Overall Experience" },
    { key: "valueForMoney", label: "Value for Money" },
  ];

  return (
    <>
      <Card
        sx={{
          boxShadow: 0,
          bgcolor: "background.neutral",
          ...sx,
        }}
        {...other}
      >
        <DialogTitle>
          <Grid>
            <Stack
              direction="row"
              sx={{ mb: -1 }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" justifyContent="left">
                <Button
                  size="small"
                  type="submit"
                  variant="contained"
                  startIcon={
                    <Iconify
                      sx={{
                        right: -5,
                        position: "relative",
                        width: 30,
                        height: 30,
                      }}
                      icon="emojione-monotone:cross-mark"
                    />
                  }
                  sx={{
                    borderRadius: 2,
                    minWidth: 60,
                    minHeight: 60,
                    ml: -2,
                    mt: -2,
                    mb: 1,
                  }}
                  onClick={handleCloseModal}
                />
              </Stack>
              <Stack
                direction="row"
                sx={{ mt: -2, ml: -6 }}
                justifyContent="center"
              >
                <Typography variant="h6">Order detail</Typography>
              </Stack>
              <Stack direction="row" justifyContent="right" />
            </Stack>
          </Grid>
        </DialogTitle>
      </Card>

      <DialogContent>
        <Grid container spacing={3} mb={4}>
          <Grid
            item
            xs={12}
            md={
              singleData?.type === "web" || singleData?.type === "app" || singleData?.type ===  "store"
                ? 4
                : 6
            }
            alignItems="stretch"
          >
            <Card sx={{ mt: 2, ml: 2, mb: 4, height: "100%" }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1.5}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Date:</Typography>
                <Typography variant="body2">
                  {formatDate(new Date(singleData?.createdAt))}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Time:</Typography>
                <Typography variant="body2">
                  {" "}
                  {formatTime(new Date(singleData?.createdAt))}
                </Typography>
              </Box>
              <Divider />

              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Id:</Typography>
                <Typography variant="body2">
                  {singleData?.id ? singleData?.id : "-"}
                </Typography>
              </Box>
              <Divider />

              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Bill Number:</Typography>
                <Typography variant="body2">
                  {singleData?.billNumber ? singleData?.billNumber : "-"}
                </Typography>
              </Box>
              <Divider />

              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Origin:</Typography>
                <Typography variant="body2">
                  {singleData?.type ? singleData?.type : "-"}
                </Typography>
              </Box>
              <Divider />

              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Order Type:</Typography>
                <Typography variant="body2">{singleData?.orderType}</Typography>
              </Box>
              <Divider />

              {singleData?.orderType === "carHop" && (
                <>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    mb={1}
                    mt={1}
                    ml={3}
                    mr={3}
                  >
                    <Typography variant="body2">Car Number:</Typography>
                    {singleData?.customer?.carNumber
                      ? singleData?.customer?.carNumber
                      : "-"}
                  </Box>
                  <Divider />

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    mb={1}
                    mt={1}
                    ml={3}
                    mr={3}
                  >
                    <Typography variant="body2">Car Color:</Typography>
                    {singleData?.customer?.carColor
                      ? singleData?.customer?.carColor
                      : "-"}
                  </Box>
                  <Divider />

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    mb={1}
                    mt={1}
                    ml={3}
                    mr={3}
                  >
                    <Typography variant="body2">Notes:</Typography>
                    {singleData?.customer?.notes
                      ? singleData?.customer?.notes
                      : "-"}
                  </Box>
                  <Divider />
                </>
              )}

              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Sub Total:</Typography>
                <Typography variant="body2">
                  {" "}
                  {currencySymbol} {singleData?.subTotal}
                </Typography>
              </Box>
              <Divider />

              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Tip:</Typography>
                <Typography variant="body2">
                  {" "}
                  {currencySymbol} {singleData?.tip ? singleData?.tip : "0"}
                </Typography>
              </Box>
              <Divider />
              {(venue?.isPayingTax || singleData?.tax > 0) && (
                <>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    mb={1}
                    mt={1}
                    ml={3}
                    mr={3}
                  >
                    <Typography variant="body2">Tax:</Typography>
                    {currencySymbol} {singleData?.tax}
                  </Box>
                  <Divider />
                </>
              )}

              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Discount:</Typography>
                <Typography variant="body2">
                  {" "}
                  {currencySymbol} {singleData?.discount}
                </Typography>
              </Box>
              <Divider />
              {(venue?.isServiceFees || singleData?.serviceFees > 0) && (
                <>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                    mb={1}
                    mt={1}
                    ml={3}
                    mr={3}
                  >
                    <Typography variant="body2">Service fee:</Typography>
                    {currencySymbol} {singleData?.serviceFees}
                  </Box>
                  <Divider />
                </>
              )}
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mb={1}
                mt={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Total:</Typography>
                <Typography variant="body2">
                  {" "}
                  {currencySymbol} {singleData?.total}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
                mt={1}
                mb={1}
                ml={3}
                mr={3}
              >
                <Typography variant="body2">Payment Type:</Typography>
                <Typography variant="body2">
                  {" "}
                  {getPaymentType(singleData?.paymentType)}
                </Typography>
              </Box>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            md={
              user?.venue?.configurations?.isDineIn ||
              user?.venue?.configurations?.isPickUp
                ? 4
                : 6
            }
          >
            <Card sx={{ mt: 2, mb: 4, height: "100%" }}>
              <Stack
                direction="row"
                sx={{ bgcolor: "grey.200" }}
                justifyContent="space-between"
              >
                <Stack direction="row" justifyContent="left">
                  <Typography sx={{ ml: 3, mt: 1 }} variant="subtitle2">
                    Qty
                  </Typography>
                  <Typography sx={{ ml: 4, mt: 1 }} variant="subtitle2">
                    Item name
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="right">
                  <Typography sx={{ mr: 6, mt: 1 }} variant="subtitle2">
                    Price
                  </Typography>
                </Stack>
              </Stack>

              <CardContent>
                <Stack sx={{ mt: -2 }} spacing={1}>
                  {singleData.items.map((item, index) => (
                    <>
                      <Stack direction="row" justifyContent="space-between">
                        <Stack
                          direction="row"
                          sx={{ ml: 1 }}
                          justifyContent="left"
                        >
                          <Typography variant="body2">{item.qty} x</Typography>
                          <Typography sx={{ ml: 4 }} variant="body2">
                            {item.name}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="right">
                          <Typography sx={{ mr: 2 }} variant="body2">
                            {currencySymbol} {Number(item.qty * item.price)}
                          </Typography>
                        </Stack>
                      </Stack>
                      {item.groups &&
                        item.groups.map((group) =>
                          group.items.map((option) => (
                            <Stack
                              direction="row"
                              justifyContent="left"
                              key={option.id}
                            >
                              <Typography
                                sx={{
                                  ml: 7,
                                  mt: -1,
                                  fontSize: 12,
                                  color: "text.secondary",
                                }}
                              >
                                + {group.name}: {option.item}
                              </Typography>
                            </Stack>
                            // )
                          ))
                        )}
                      {item.notes && (
                        <Stack direction="row" justifyContent="left">
                          <Typography
                            sx={{ ml: 7, mt: -1, fontSize: 12, color: "red" }}
                          >
                            {item.notes}
                          </Typography>
                        </Stack>
                      )}
                      <Divider />
                    </>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {(singleData?.type === "web" || singleData?.type === "app" || singleData?.type ===  "store") && (
            <Grid item xs={12} md={4}>
              <Card sx={{ mt: 2, mr: 2, mb: 4, height: "100%" }}>
                <Stack
                  direction="row"
                  sx={{ bgcolor: "grey.200" }}
                  justifyContent="space-between"
                >
                  <Stack direction="row" justifyContent="left">
                    <Typography sx={{ ml: 3, mt: 1 }} variant="subtitle2">
                      Review
                    </Typography>
                  </Stack>
                </Stack>

                <CardContent>
                  {reviews && reviews ? (
                    <>
                      <Grid item xs={12} sx={{ mb: 2 }}>
                        {reviews &&
                          reviews.overallOrderRating &&
                          questions.map((question) => (
                            <Box
                              key={question.key}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 1,
                                justifyContent: "space-between",
                                gap: "10px",
                              }}
                            >
                              <Box>
                                <Typography
                                  sx={{
                                    flexGrow: 1,
                                    fontSize: {
                                      xs: "13px",
                                      sm: "14px",
                                      md: "14px",
                                    },
                                  }}
                                >
                                  {question.label}
                                </Typography>
                              </Box>
                              <Box>
                                {renderStars(
                                  reviews.overallOrderRating[question.key]
                                )}
                              </Box>
                            </Box>
                          ))}
                      </Grid>

                      {reviews && reviews?.itemsRating?.length > 0 && (
                        <Grid xs={12} sx={{ mb: 2 }}>
                          <Divider />
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 2 }}>
                            Items
                          </Typography>

                          <Box>
                            {singleData?.items?.map((item, index) => {
                              const itemRating =
                                reviews?.itemsRating?.find(
                                  (rating) => rating.id === item.id
                                )?.itemRating || 0;
                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                  mb={1}
                                  mt={1}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      gap: "10px",
                                    }}
                                  >
                                    <Image
                                      src={
                                        imageURLs[index]
                                          ? imageURLs[index]
                                          : "/assets/placeholder.png"
                                      }
                                      alt={"image"}
                                      style={{
                                        height: "50px",
                                        width: "50px",
                                        borderRadius: "5px",
                                      }}
                                    />
                                    <Box>
                                      <Box
                                        sx={{
                                          fontSize: {
                                            xs: "13px",
                                            sm: "14px",
                                            md: "14px",
                                          },
                                        }}
                                      >
                                        {fNumber(item?.qty, 0)} X {item?.name}
                                      </Box>

                                      {item?.groups &&
                                        item?.groups?.length > 0 && (
                                          <>
                                            {item?.selectedVariant && (
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Typography
                                                  sx={{
                                                    fontSize: 12,
                                                    fontWeight: "bold",
                                                    color: "#666",
                                                  }}
                                                >
                                                  Variant:
                                                </Typography>
                                                <Typography
                                                  sx={{ ml: 1, fontSize: 12 }}
                                                >
                                                  {item?.selectedVariant?.name}
                                                </Typography>
                                              </Box>
                                            )}
                                            <>
                                              {item?.groups?.map((sauce) => (
                                                <Typography
                                                  key={index}
                                                  variant="caption"
                                                  sx={{
                                                    color: "#666",
                                                    display: "flex",
                                                    gap: "2px",
                                                    fontWeight: "bold",
                                                    "@media (max-width: 600px)":
                                                      {
                                                        fontSize: "10px",
                                                      },
                                                  }}
                                                >
                                                  {sauce?.name} :
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      color: "#666",
                                                      display: "flex",
                                                      marginLeft: "5px",
                                                      gap: "2px",
                                                      "@media (max-width: 600px)":
                                                        {
                                                          fontSize: "10px",
                                                        },
                                                    }}
                                                  >
                                                    {sauce?.items?.map(
                                                      (item, index) => (
                                                        <span key={index}>
                                                          {item?.item}
                                                          {index !==
                                                            sauce.items.length -
                                                              1 && ", "}
                                                        </span>
                                                      )
                                                    )}
                                                  </Typography>
                                                </Typography>
                                              ))}
                                            </>
                                          </>
                                        )}

                                      <Box
                                        sx={{ fontSize: "14px", color: "red" }}
                                      >
                                        {item?.notes}
                                      </Box>

                                      <Box
                                        sx={{
                                          mt: 1,
                                          ml: -0.5,
                                        }}
                                      >
                                        <Box>{renderStars(itemRating)}</Box>
                                      </Box>
                                    </Box>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Grid>
                      )}

                      {reviews && reviews?.comments && (
                        <>
                          <Typography variant="subtitle1" sx={{ mt: 1 }}>
                            Comments:
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2, mt: 1 }}>
                            {reviews.comments}
                          </Typography>
                        </>
                      )}
                    </>
                  ) : (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontSize: {
                          xs: "14px",
                          sm: "16px",
                          md: "16px",
                        },
                      }}
                    >
                      Review for this order is not submitted yet!
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </>
  );
}
